// Pattern-based extraction engine for meeting notes.
// 100% offline. No API calls.

export type MeetingType =
  | "discovery"
  | "sprint_planning"
  | "standup"
  | "client_review"
  | "retrospective"
  | "workshop";

export interface ActionItem {
  task: string;
  owner: string;
  deadline: string;
  priority: "High" | "Medium" | "Low";
}

export interface SuggestedRequirement {
  source: string;
  text: string;
  priority: "High" | "Medium" | "Low";
}

export interface MeetingResult {
  type: MeetingType;
  date: string;
  decisions: string[];
  action_items: ActionItem[];
  open_questions: string[];
  key_discussions: string[];
  suggested_requirements: SuggestedRequirement[];
  sections: Record<string, string[]>;
}

export const MEETING_TEMPLATES: Record<
  MeetingType,
  { label: string; sections: { name: string; prompts: string[] }[] }
> = {
  discovery: {
    label: "Discovery",
    sections: [
      { name: "Client Context", prompts: ["Industry/Sector", "Current Pain Points", "Existing Solutions", "Business Goals"] },
      { name: "Requirements Elicited", prompts: ["Must-Have Features", "Nice-to-Have Features", "Constraints", "Success Metrics"] },
      { name: "Decisions Made", prompts: ["Approach Selected", "Technology Direction", "Timeline Agreement", "Budget Range"] },
      { name: "Action Items", prompts: ["Follow-up Tasks", "Document Requests", "Stakeholder Introductions", "Proposal Due Date"] },
      { name: "Open Questions", prompts: ["Technical Clarifications Needed", "Business Questions", "Assumptions to Validate"] },
    ],
  },
  sprint_planning: {
    label: "Sprint Planning",
    sections: [
      { name: "Sprint Goal", prompts: ["Primary Objective", "Success Criteria"] },
      { name: "Stories Committed", prompts: ["Story ID", "Story Title", "Story Points", "Owner", "Dependencies"] },
      { name: "Capacity", prompts: ["Team Availability", "Velocity Forecast", "Risk Factors"] },
      { name: "Decisions", prompts: ["Scope Inclusions", "Scope Exclusions", "Technical Approach"] },
      { name: "Action Items", prompts: ["Setup Tasks", "Research Tasks", "Stakeholder Updates"] },
    ],
  },
  standup: {
    label: "Standup",
    sections: [
      { name: "Progress Updates", prompts: ["Completed Since Last Standup", "In Progress", "Blockers"] },
      { name: "Decisions", prompts: ["Technical Decisions", "Scope Adjustments"] },
      { name: "Action Items", prompts: ["Blocker Resolution", "Support Requests", "Follow-ups"] },
    ],
  },
  client_review: {
    label: "Client Review",
    sections: [
      { name: "Demo Notes", prompts: ["Features Shown", "Client Reactions", "Feedback Captured"] },
      { name: "Decisions", prompts: ["Change Requests Approved", "Priority Shifts", "Timeline Adjustments"] },
      { name: "Action Items", prompts: ["Bug Fixes", "Enhancement Requests", "Documentation Updates", "Next Review Date"] },
      { name: "Open Questions", prompts: ["Pending Approvals", "Clarification Needed", "Scope Questions"] },
    ],
  },
  retrospective: {
    label: "Retrospective",
    sections: [
      { name: "What Went Well", prompts: ["Wins", "Strengths"] },
      { name: "What Didn't", prompts: ["Issues", "Friction Points"] },
      { name: "Decisions", prompts: ["Process Changes", "Experiments"] },
      { name: "Action Items", prompts: ["Improvements", "Owners"] },
    ],
  },
  workshop: {
    label: "Workshop",
    sections: [
      { name: "Topics Covered", prompts: ["Discussions", "Brainstorm Outputs"] },
      { name: "Decisions", prompts: ["Direction Set", "Approaches Chosen"] },
      { name: "Action Items", prompts: ["Next Steps", "Owners"] },
      { name: "Open Questions", prompts: ["Unresolved", "Parking Lot"] },
    ],
  },
};

const DECISION_PATTERNS: RegExp[] = [
  /(?:decided|agreed|resolved|concluded|determined)\s+(?:that\s+)?([^.\n]+)/gi,
  /(?:we will|we'll|let's|going to)\s+([^.\n]+)/gi,
  /(?:approved|accepted|selected|chose)\s+([^.\n]+)/gi,
];

const ACTION_PATTERNS: RegExp[] = [
  /(?:action item|todo|task|follow.?up)\s*:?\s*([^.\n]+)/gi,
  /\b([A-Z][a-z]+)\s+(will|to|needs to|should|is going to)\s+([^.\n]+)/g,
  /(?:assign|delegate|responsible)\s+(?:to\s+)?([A-Z][a-z]+)\s*:?\s*([^.\n]+)/gi,
];

const QUESTION_PATTERNS: RegExp[] = [
  /(?:question|clarify|unclear|need to know|find out)\s*:?\s*([^.\n?]+\??)/gi,
  /((?:what about|how do we|can we|should we|why are|when will|where do)[^.\n?]+\?)/gi,
];

const HIGH_PRIORITY = /\b(urgent|asap|blocker|critical|immediately|today|blocking)\b/i;
const LOW_PRIORITY = /\b(follow.?up|consider|maybe|nice to have|eventually|low priority)\b/i;

function extractDeadline(text: string): string | null {
  const patterns = [
    /by\s+(tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday|next week|end of week|end of day|eod|cob|end of month|eom)/i,
    /by\s+(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)/,
    /due\s+(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)/i,
    /(?:by|before)\s+([A-Z][a-z]+ \d{1,2}(?:st|nd|rd|th)?)/,
  ];
  for (const p of patterns) {
    const m = text.match(p);
    if (m) return m[1] || m[0];
  }
  return null;
}

function detectPriority(text: string): "High" | "Medium" | "Low" {
  if (HIGH_PRIORITY.test(text)) return "High";
  if (LOW_PRIORITY.test(text)) return "Low";
  return "Medium";
}

function dedupe(arr: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of arr) {
    const key = item.toLowerCase().replace(/\s+/g, " ").trim();
    if (key.length < 6) continue;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(item.trim());
    }
  }
  return out;
}

export function extractMeetingStructure(
  rawNotes: string,
  meetingType: MeetingType,
): MeetingResult {
  const result: MeetingResult = {
    type: meetingType,
    date: new Date().toISOString().split("T")[0],
    decisions: [],
    action_items: [],
    open_questions: [],
    key_discussions: [],
    suggested_requirements: [],
    sections: {},
  };

  // Decisions
  const rawDecisions: string[] = [];
  for (const pattern of DECISION_PATTERNS) {
    const matches = rawNotes.matchAll(pattern);
    for (const m of matches) {
      const cleaned = m[0]
        .replace(
          /^(?:decided|agreed|resolved|concluded|determined|we will|we'll|let's|going to|approved|accepted|selected|chose)\s+(?:that\s+)?/i,
          "",
        )
        .replace(/[.,;]+$/, "")
        .trim();
      if (cleaned.length > 8) rawDecisions.push(cleaned);
    }
  }
  result.decisions = dedupe(rawDecisions);

  // Actions
  // Words that look like names (Capitalized) but are common sentence-starters.
  // Without this list, "Decided to ship..." treats "Decided" as the owner.
  const NON_NAMES = new Set([
    "Need", "Needs", "Decided", "Agreed", "Approved", "Accepted", "Action",
    "Todo", "Task", "Question", "Unclear", "Urgent", "Note", "Notes", "Follow",
    "Followup", "We", "They", "Let", "Going", "Let's", "Lets", "I", "Also",
    "Then", "Now", "Today", "Tomorrow", "Yesterday", "Next", "Last", "First",
    "Final", "TBD", "TODO",
  ]);
  const isLikelyName = (w: string) => !!w && !NON_NAMES.has(w);

  const actionSeen = new Set<string>();
  for (const pattern of ACTION_PATTERNS) {
    const matches = rawNotes.matchAll(pattern);
    for (const m of matches) {
      const full = m[0];
      let owner = "TBD";
      const ownerMatch = full.match(/\b([A-Z][a-z]+)\s+(?:will|needs to|should|is going to|to)\b/);
      if (ownerMatch && isLikelyName(ownerMatch[1])) owner = ownerMatch[1];
      const explicitAssign = full.match(/(?:assign|delegate|responsible)\s+(?:to\s+)?([A-Z][a-z]+)/i);
      if (explicitAssign && isLikelyName(explicitAssign[1])) owner = explicitAssign[1];

      const task = full
        .replace(/^(?:action item|todo|task|follow.?up)\s*:?\s*/i, "")
        .replace(/^[A-Z][a-z]+\s+(?:will|needs to|should|is going to|to)\s+/, "")
        .replace(/[.,;]+$/, "")
        .trim();

      const key = task.toLowerCase();
      if (task.length < 5 || actionSeen.has(key)) continue;
      actionSeen.add(key);

      result.action_items.push({
        task,
        owner,
        deadline: extractDeadline(full) ?? "TBD",
        priority: detectPriority(full),
      });
    }
  }

  // Questions
  const rawQuestions: string[] = [];
  for (const pattern of QUESTION_PATTERNS) {
    const matches = rawNotes.matchAll(pattern);
    for (const m of matches) {
      let q = (m[1] || m[0]).trim().replace(/[.;]+$/, "");
      if (!q.endsWith("?")) q += "?";
      if (q.length > 12) rawQuestions.push(q);
    }
  }
  result.open_questions = dedupe(rawQuestions);

  // Key discussion bullets (lines that look meaningful)
  result.key_discussions = rawNotes
    .split(/\n+/)
    .map((l) => l.replace(/^[-*•\d.)\s]+/, "").trim())
    .filter((l) => l.length > 20 && l.length < 240)
    .slice(0, 12);

  // Suggested requirements from feature-ish decisions
  const featureKeywords = /(feature|function|capability|support|integration|module|workflow|enable|allow|provide)/i;
  for (const d of result.decisions) {
    if (featureKeywords.test(d)) {
      const text = `The system shall ${d
        .toLowerCase()
        .replace(/^(we will|we'll|let's|going to|decided to)\s+/i, "")
        .replace(/\.$/, "")}`;
      result.suggested_requirements.push({
        source: "Meeting decision",
        text,
        priority: "High",
      });
    }
  }

  // Flag unclear questions
  result.open_questions = result.open_questions.map((q) =>
    /\b(maybe|unclear|not sure|tbd|assume)\b/i.test(q) ? `${q} [NEEDS CLARIFICATION]` : q,
  );

  // Pre-populate template sections
  const template = MEETING_TEMPLATES[meetingType];
  for (const section of template.sections) {
    result.sections[section.name] = [];
  }
  if ("Decisions Made" in result.sections) result.sections["Decisions Made"] = result.decisions;
  if ("Decisions" in result.sections) result.sections["Decisions"] = result.decisions;
  if ("Action Items" in result.sections)
    result.sections["Action Items"] = result.action_items.map(
      (a) => `${a.task} — ${a.owner} (${a.deadline})`,
    );
  if ("Open Questions" in result.sections) result.sections["Open Questions"] = result.open_questions;

  return result;
}
